/**
 * Koekje met foto.
 *
 * De klant kiest een foto en ziet hem meteen op het koekje op het plankje. Slepen verschuift
 * hem, knijpen, ctrl+scrollen, de schuifregelaar en plus/min zoomen. De foto bedekt altijd
 * het hele vierkant om de cirkel, dus er komt nooit een lege rand op de print.
 *
 * Bij het toevoegen aan de winkelwagen maakt dit script van de uitsnede een vierkante PNG op
 * printformaat (6,3 cm op 300 dpi is 744 bij 744 pixels, met die dpi in het bestand zelf) en
 * stopt die in een file input van het productformulier. Het thema verstuurt zijn formulier
 * als FormData, dus Shopify slaat het bestand op als line item property: een link bij de
 * bestelling in het beheer.
 *
 * Het maken van dat bestand gaat asynchroon, dus de submit wordt eerst onderschept en na het
 * klaarzetten opnieuw verstuurd, waarna de gewone afhandeling van het thema het overneemt.
 *
 * Alle maten staan intern in pixels van de originele foto: `centerX`/`centerY` is het punt
 * dat in het midden van de cirkel staat, `zoom` 1 betekent dat de korte zijde van de foto
 * precies de cirkel vult. Daardoor maakt het formaat op het scherm niet uit.
 */
(function () {
  'use strict';

  if (customElements.get('photo-cookie')) return;

  var MM_PER_INCH = 25.4;
  var MAX_ZOOM = 4;
  var LOW_DPI = 150; // daaronder is een print zichtbaar onscherp
  var MAX_FILE_BYTES = 40 * 1024 * 1024;
  var MAX_CANVAS_SIDE = 4096; // blijft ruim binnen de canvaslimiet van iOS Safari
  var KEY_STEP = 0.02; // deel van de cirkel per pijltjestoets, met shift vijf keer zo veel
  var FILE_NAME = 'koekje-met-foto';
  var AUTO_ZOOM_BELOW = 520; // breedte van het plankje in pixels waaronder de weergave na uploaden inzoomt

  var CRC_TABLE = (function () {
    var table = new Uint32Array(256);
    for (var n = 0; n < 256; n++) {
      var c = n;
      for (var k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      table[n] = c >>> 0;
    }
    return table;
  })();

  function crc32(bytes) {
    var crc = 0xffffffff;
    for (var i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    return (crc ^ 0xffffffff) >>> 0;
  }

  /**
   * Zet de resolutie in de PNG (pHYs chunk), zodat het bestand in printsoftware meteen op
   * 6,3 bij 6,3 cm opent in plaats van op de standaard 72 dpi.
   */
  function withPrintResolution(png, dpi) {
    var ppm = Math.round(dpi / 0.0254);
    var view = new DataView(png.buffer, png.byteOffset, png.byteLength);

    var offset = 8;
    while (offset + 8 <= png.length) {
      var length = view.getUint32(offset);
      var type = String.fromCharCode(png[offset + 4], png[offset + 5], png[offset + 6], png[offset + 7]);
      if (type === 'pHYs') {
        view.setUint32(offset + 8, ppm);
        view.setUint32(offset + 12, ppm);
        png[offset + 16] = 1;
        view.setUint32(offset + 17, crc32(png.subarray(offset + 4, offset + 17)));
        return png;
      }
      if (type === 'IDAT') break;
      offset += 12 + length;
    }

    var chunk = new Uint8Array(21);
    var chunkView = new DataView(chunk.buffer);
    chunkView.setUint32(0, 9);
    chunk.set([0x70, 0x48, 0x59, 0x73], 4); // pHYs
    chunkView.setUint32(8, ppm);
    chunkView.setUint32(12, ppm);
    chunk[16] = 1; // eenheid: meter
    chunkView.setUint32(17, crc32(chunk.subarray(4, 17)));

    var afterHeader = 8 + 12 + view.getUint32(8); // IHDR is altijd de eerste chunk
    var out = new Uint8Array(png.length + chunk.length);
    out.set(png.subarray(0, afterHeader), 0);
    out.set(chunk, afterHeader);
    out.set(png.subarray(afterHeader), afterHeader + chunk.length);
    return out;
  }

  function makeCanvas(size) {
    var canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    return canvas;
  }

  function canvasToBlob(canvas) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob);
        else reject(new Error('Canvas kon geen PNG maken'));
      }, 'image/png');
    });
  }

  function blobToBytes(blob) {
    if (blob.arrayBuffer) {
      return blob.arrayBuffer().then(function (buffer) {
        return new Uint8Array(buffer);
      });
    }
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        resolve(new Uint8Array(reader.result));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      image.decoding = 'async';
      image.onload = function () {
        if (!image.naturalWidth || !image.naturalHeight) return reject(new Error('Lege foto'));
        /* decode() voorkomt een hapering bij het tonen, maar blijft hangen zolang de pagina
           verborgen is, zoals op een telefoon terwijl de fotokiezer openstaat. Na onload is de
           foto al bruikbaar, dus wacht er niet langer dan even op. */
        var decoded = image.decode ? image.decode().catch(function () {}) : Promise.resolve();
        var shortWait = new Promise(function (done) {
          setTimeout(done, 300);
        });
        Promise.race([decoded, shortWait]).then(function () {
          resolve(image);
        });
      };
      image.onerror = function () {
        reject(new Error('Foto kon niet worden geladen'));
      };
      image.src = url;
    });
  }

  function clampNumber(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /**
   * Vangnet voor browsers zonder DataTransfer: dan krijgt de file input geen bestand, en
   * voegt de FormData van het productformulier het zelf toe.
   */
  function patchProductForm() {
    var ProductForm = customElements.get('product-form');
    if (!ProductForm || ProductForm.prototype.__photoCookie) return;

    var getFormData = ProductForm.prototype.getFormData;
    ProductForm.prototype.getFormData = function () {
      var formData = getFormData.apply(this, arguments);
      var form = this.form;
      document.querySelectorAll('photo-cookie').forEach(function (element) {
        if (typeof element.contributeTo === 'function') element.contributeTo(formData, form);
      });
      return formData;
    };
    ProductForm.prototype.__photoCookie = true;
  }

  class PhotoCookie extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.strings = {};
      try {
        this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      } catch (error) {}

      this.stage = this.querySelector('[data-stage]');
      this.windowElement = this.querySelector('[data-window]');
      this.photoElement = this.querySelector('[data-photo]');
      this.picker = this.querySelector('[data-picker]');
      this.output = this.querySelector('[data-output]');
      this.zoomControls = this.querySelector('[data-zoom-controls]');
      this.zoomRange = this.querySelector('[data-zoom]');
      this.viewToggle = this.querySelector('[data-view-toggle]');
      this.viewLabel = this.querySelector('[data-view-label]');
      this.pickLabel = this.querySelector('[data-pick-label]');
      this.statusElement = this.querySelector('[data-status]');

      this.photoMm = parseFloat(this.dataset.photoMm) || 63;
      this.dpi = parseInt(this.dataset.dpi, 10) || 300;
      this.formId = this.dataset.formId;
      this.productId = this.dataset.productId;

      this.image = null;
      this.objectUrl = null;
      this.pendingFile = null;
      this.pointers = new Map();
      this.touchInput = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

      this.querySelectorAll('[data-pick]').forEach((button) => {
        button.addEventListener('click', () => this.picker.click());
      });
      this.picker.addEventListener('change', () => {
        var file = this.picker.files && this.picker.files[0];
        this.picker.value = '';
        if (file) this.loadFile(file);
      });

      this.zoomRange.addEventListener('input', () => {
        this.zoomAround(parseFloat(this.zoomRange.value), this.diameter / 2, this.diameter / 2);
      });
      this.querySelectorAll('[data-zoom-step]').forEach((button) => {
        button.addEventListener('click', () => {
          var factor = button.dataset.zoomStep === '1' ? 1.25 : 0.8;
          this.zoomAround(this.zoom * factor, this.diameter / 2, this.diameter / 2);
        });
      });
      this.viewToggle.addEventListener('click', () => {
        this.setView(this.dataset.view === 'cookie' ? 'board' : 'cookie');
      });

      this.windowElement.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.windowElement.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.windowElement.addEventListener('pointerup', this.onPointerEnd.bind(this));
      this.windowElement.addEventListener('pointercancel', this.onPointerEnd.bind(this));
      this.windowElement.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
      this.windowElement.addEventListener('keydown', this.onKeyDown.bind(this));

      this.measure();
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.measure();
          this.render();
        });
        this.resizeObserver.observe(this.windowElement);
      }

      this.onSubmit = this.onSubmit.bind(this);
      this.onAdded = this.onResult.bind(this, true);
      this.onError = this.onResult.bind(this, false);
      document.addEventListener('submit', this.onSubmit, true);
      document.addEventListener('product-ajax:added', this.onAdded);
      document.addEventListener('product-ajax:error', this.onError);
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('product-ajax:added', this.onAdded);
      document.removeEventListener('product-ajax:error', this.onError);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.initialized = false;
    }

    /* ---------- Foto laden ---------- */

    loadFile(file) {
      if (file.type && file.type.indexOf('image/') !== 0) return this.showStatus('notImage', 'error');
      if (file.size > MAX_FILE_BYTES) return this.showStatus('tooLarge', 'error');

      this.showStatus('loading');
      this.classList.add('is-loading');

      var url = URL.createObjectURL(file);
      return loadImage(url)
        .then((image) => {
          if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
          this.objectUrl = url;
          this.image = image;
          this.imageWidth = image.naturalWidth;
          this.imageHeight = image.naturalHeight;
          this.shortSide = Math.min(this.imageWidth, this.imageHeight);
          this.zoom = 1;
          this.centerX = this.imageWidth / 2;
          this.centerY = this.imageHeight / 2;

          this.photoElement.src = url;
          this.photoElement.hidden = false;
          this.classList.add('has-photo');
          this.windowElement.tabIndex = 0;
          this.zoomControls.hidden = false;
          this.viewToggle.hidden = false;
          this.pickLabel.textContent = this.strings.change;
          this.clearOutput();

          this.measure();
          /* Op een smal scherm is de cirkel op het hele plankje te klein om te schuiven, dus
             daar zoomt de weergave in. Breed blijft het hele plankje in beeld. */
          this.setView(this.stage.offsetWidth < AUTO_ZOOM_BELOW ? 'cookie' : 'board');
          this.render();
          this.showHint();
        })
        .catch(() => {
          URL.revokeObjectURL(url);
          this.showStatus('unreadable', 'error');
        })
        .then(() => {
          this.classList.remove('is-loading');
        });
    }

    /* ---------- Weergave ---------- */

    setView(view) {
      this.dataset.view = view;
      var zoomedIn = view === 'cookie';
      this.viewToggle.setAttribute('aria-pressed', zoomedIn ? 'true' : 'false');
      this.viewLabel.textContent = zoomedIn ? this.strings.board : this.strings.zoomIn;
    }

    measure() {
      this.diameter = this.windowElement.offsetWidth || 1;
    }

    /* Pixels op het scherm per pixel van de foto. */
    get scale() {
      return (this.diameter / this.shortSide) * this.zoom;
    }

    /* Houdt de foto groot genoeg en binnen de cirkel: geen lege randen op de print. */
    constrain() {
      this.zoom = clampNumber(this.zoom, 1, MAX_ZOOM);
      var half = this.shortSide / this.zoom / 2;
      this.centerX = clampNumber(this.centerX, half, this.imageWidth - half);
      this.centerY = clampNumber(this.centerY, half, this.imageHeight - half);
    }

    render() {
      if (!this.image) return;
      this.constrain();

      var scale = this.scale;
      var x = this.diameter / 2 - this.centerX * scale;
      var y = this.diameter / 2 - this.centerY * scale;

      var style = this.photoElement.style;
      style.width = this.imageWidth * scale + 'px';
      style.height = this.imageHeight * scale + 'px';
      style.transform = 'translate3d(' + x + 'px, ' + y + 'px, 0)';

      this.zoomRange.value = this.zoom;
      this.zoomRange.style.setProperty('--ss-zoom-fill', ((this.zoom - 1) / (MAX_ZOOM - 1)) * 100 + '%');
      this.updateResolutionHint();
    }

    /* Verschuift de foto met een afstand in cirkelpixels. */
    panBy(dx, dy) {
      var scale = this.scale;
      this.centerX -= dx / scale;
      this.centerY -= dy / scale;
      this.render();
    }

    /* Zoomt zo dat het punt onder (anchorX, anchorY) in de cirkel blijft staan. */
    zoomAround(zoom, anchorX, anchorY) {
      if (!this.image) return;
      var before = this.scale;
      var pointX = this.centerX + (anchorX - this.diameter / 2) / before;
      var pointY = this.centerY + (anchorY - this.diameter / 2) / before;

      this.zoom = clampNumber(zoom, 1, MAX_ZOOM);
      var after = this.scale;
      this.centerX = pointX - (anchorX - this.diameter / 2) / after;
      this.centerY = pointY - (anchorY - this.diameter / 2) / after;
      this.render();
    }

    /* Schermcoordinaten naar cirkelpixels, ook als het plankje ingezoomd staat. */
    toWindow(clientX, clientY) {
      var rect = this.windowElement.getBoundingClientRect();
      var ratio = rect.width / this.diameter || 1;
      return { x: (clientX - rect.left) / ratio, y: (clientY - rect.top) / ratio, ratio: ratio };
    }

    /* ---------- Invoer ---------- */

    onPointerDown(event) {
      if (!this.image) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      event.preventDefault();
      try {
        this.windowElement.setPointerCapture(event.pointerId);
      } catch (error) {}
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.classList.add('is-dragging');
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();

      var points = Array.from(this.pointers.values());
      var before = this.gestureOf(points);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      var after = this.gestureOf(Array.from(this.pointers.values()));

      var start = this.toWindow(before.x, before.y);
      var end = this.toWindow(after.x, after.y);

      if (this.pointers.size > 1 && before.distance > 0) {
        this.zoomAround(this.zoom * (after.distance / before.distance), start.x, start.y);
      }
      this.panBy(end.x - start.x, end.y - start.y);
    }

    onPointerEnd(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      try {
        if (this.windowElement.hasPointerCapture(event.pointerId)) {
          this.windowElement.releasePointerCapture(event.pointerId);
        }
      } catch (error) {}
      if (!this.pointers.size) this.classList.remove('is-dragging');
    }

    /* Midden en onderlinge afstand van de vingers. */
    gestureOf(points) {
      if (points.length < 2) return { x: points[0].x, y: points[0].y, distance: 0 };
      var a = points[0];
      var b = points[1];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, distance: Math.hypot(a.x - b.x, a.y - b.y) };
    }

    /* Alleen knijpen op een trackpad (ctrl + scroll): gewoon scrollen blijft de pagina. */
    onWheel(event) {
      if (!this.image || !event.ctrlKey) return;
      event.preventDefault();
      var point = this.toWindow(event.clientX, event.clientY);
      this.zoomAround(this.zoom * Math.exp(-event.deltaY * 0.01), point.x, point.y);
    }

    onKeyDown(event) {
      if (!this.image) return;
      var step = this.diameter * KEY_STEP * (event.shiftKey ? 5 : 1);
      var middle = this.diameter / 2;

      switch (event.key) {
        case 'ArrowLeft':
          this.panBy(-step, 0);
          break;
        case 'ArrowRight':
          this.panBy(step, 0);
          break;
        case 'ArrowUp':
          this.panBy(0, -step);
          break;
        case 'ArrowDown':
          this.panBy(0, step);
          break;
        case '+':
        case '=':
          this.zoomAround(this.zoom * 1.1, middle, middle);
          break;
        case '-':
        case '_':
          this.zoomAround(this.zoom / 1.1, middle, middle);
          break;
        default:
          return;
      }
      event.preventDefault();
    }

    /* ---------- Meldingen ---------- */

    showStatus(key, type) {
      var message = this.strings[key] || key;
      this.statusElement.textContent = message;
      this.statusElement.hidden = !message;
      this.statusElement.classList.toggle('is-error', type === 'error');
      this.statusElement.classList.toggle('is-warning', type === 'warning');
      this.statusElement.classList.toggle('is-success', type === 'success');
      this.statusKey = key;
    }

    showHint() {
      this.showStatus(this.touchInput ? 'hintTouch' : 'hintPointer');
      this.updateResolutionHint();
    }

    /* Waarschuwt als de uitsnede minder dan LOW_DPI pixels per inch op de print heeft. Is de
       foto zelf al te klein, dan helpt uitzoomen niet en zegt de melding dat ook niet. */
    updateResolutionHint() {
      var dpi = this.shortSide / this.zoom / (this.photoMm / MM_PER_INCH);
      var warning = null;
      if (dpi < LOW_DPI) warning = this.zoom > 1.01 ? 'lowResolution' : 'lowResolutionPhoto';

      var showing = this.statusKey === 'lowResolution' || this.statusKey === 'lowResolutionPhoto';
      if (warning && this.statusKey !== warning) {
        this.showStatus(warning, 'warning');
      } else if (!warning && showing) {
        this.showStatus(this.touchInput ? 'hintTouch' : 'hintPointer');
      }
    }

    flagMissingPhoto() {
      this.showStatus('required', 'error');
      this.classList.remove('is-missing');
      void this.offsetWidth; // start de animatie opnieuw
      this.classList.add('is-missing');
      this.stage.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    /* ---------- Toevoegen aan de winkelwagen ---------- */

    ownsForm(form) {
      if (!form || form.tagName !== 'FORM') return false;
      if (form.id === this.formId) return true;
      return form.matches('form[action*="/cart/add"]') && form.dataset.productId === this.productId;
    }

    onSubmit(event) {
      var form = event.target;
      if (!this.ownsForm(form)) return;
      if (this.passThrough) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!this.image) return this.flagMissingPhoto();
      if (this.preparing) return;
      this.prepareAndSubmit(form, event.submitter);
    }

    prepareAndSubmit(form, submitter) {
      var productForm = form.closest('product-form');
      this.preparing = true;
      if (productForm) productForm.classList.add('m-spinner-loading');

      return this.createPrintFile()
        .then((file) => {
          this.attach(file);
          this.preparing = false;
          this.awaitingResult = true;
          this.passThrough = true;
          try {
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
            } else {
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          } finally {
            this.passThrough = false;
          }
        })
        .catch((error) => {
          console.error(error);
          this.preparing = false;
          if (productForm) productForm.classList.remove('m-spinner-loading');
          this.showStatus('failed', 'error');
        });
    }

    /* De uitsnede als vierkante PNG op printformaat. */
    createPrintFile() {
      var size = Math.round((this.photoMm / MM_PER_INCH) * this.dpi);
      var side = this.shortSide / this.zoom;
      var source = this.image;
      var sourceX = this.centerX - side / 2;
      var sourceY = this.centerY - side / 2;
      var sourceSide = side;

      /* In stappen van hooguit de helft verkleinen: in een keer van 3000 naar 744 pixels
         wordt in Safari korrelig. */
      while (sourceSide > size * 2) {
        var next = Math.max(size, Math.min(MAX_CANVAS_SIDE, Math.round(sourceSide / 2)));
        var step = makeCanvas(next);
        var stepContext = step.getContext('2d');
        stepContext.imageSmoothingEnabled = true;
        stepContext.imageSmoothingQuality = 'high';
        stepContext.drawImage(source, sourceX, sourceY, sourceSide, sourceSide, 0, 0, next, next);
        source = step;
        sourceX = 0;
        sourceY = 0;
        sourceSide = next;
      }

      var canvas = makeCanvas(size);
      var context = canvas.getContext('2d');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, size, size);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(source, sourceX, sourceY, sourceSide, sourceSide, 0, 0, size, size);

      var name = FILE_NAME + '-' + Math.round(this.photoMm) + 'mm.png';
      return canvasToBlob(canvas)
        .then(blobToBytes)
        .then((bytes) => new File([withPrintResolution(bytes, this.dpi)], name, { type: 'image/png' }));
    }

    attach(file) {
      this.pendingFile = null;
      try {
        var transfer = new DataTransfer();
        transfer.items.add(file);
        this.output.files = transfer.files;
      } catch (error) {}

      if (!this.output.files || this.output.files.length !== 1) {
        this.pendingFile = file;
        patchProductForm();
      }
    }

    contributeTo(formData, form) {
      if (this.pendingFile && this.ownsForm(form)) {
        formData.set(this.output.name, this.pendingFile, this.pendingFile.name);
      }
    }

    clearOutput() {
      this.pendingFile = null;
      try {
        this.output.value = '';
      } catch (error) {}
    }

    onResult(added) {
      if (!this.awaitingResult) return;
      this.awaitingResult = false;
      this.clearOutput();
      if (added) this.showStatus('added', 'success');
    }
  }

  customElements.define('photo-cookie', PhotoCookie);
})();
